import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Folder, Home, ChevronRight, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

// Modal folder picker — lets the user drill through folders and pick one.
// `excludeIds` skips items that should not be selectable as a destination
// (e.g. the files being moved so you can't move them into themselves).
const FolderPicker = ({ open, onOpenChange, title = "Select destination", confirmLabel = "Move here", excludeIds = [], onConfirm }) => {
  const [parent, setParent] = useState("root");
  const [items, setItems] = useState([]);
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setParent("root");
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    setLoading(true);
    api.get("/files", { params: { parent } })
      .then((r) => {
        if (!alive) return;
        setItems((r.data.items || []).filter((i) => i.is_folder && !excludeIds.includes(i.file_id)));
        setBreadcrumb(r.data.breadcrumb || []);
      })
      .catch(() => { if (alive) setItems([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, parent, excludeIds]);

  const submit = async () => {
    setBusy(true);
    try { await onConfirm(parent); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white text-gray-800 sm:max-w-md" data-testid="folder-picker-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="text-gray-500">Pick a folder from your library.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-1 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-xs">
          <button onClick={() => setParent("root")} className="flex items-center gap-1 font-medium text-gray-700 hover:text-[#0364b8]">
            <Home className="h-3.5 w-3.5" /> My files
          </button>
          {breadcrumb.map((c) => (
            <span key={c.file_id} className="flex items-center gap-1">
              <ChevronRight className="h-3 w-3 text-gray-400" />
              <button onClick={() => setParent(c.file_id)} className="text-gray-700 hover:text-[#0364b8]">{c.name}</button>
            </span>
          ))}
        </div>
        <div className="max-h-64 overflow-y-auto rounded border border-gray-200">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-sm text-gray-400"><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…</div>
          ) : items.length === 0 ? (
            <div className="px-3 py-6 text-center text-xs text-gray-400">No subfolders here.</div>
          ) : (
            items.map((it) => (
              <button
                key={it.file_id}
                onDoubleClick={() => setParent(it.file_id)}
                onClick={() => setParent(it.file_id)}
                data-testid={`folder-picker-item-${it.file_id}`}
                className="flex w-full items-center gap-2 border-b border-gray-100 px-3 py-2 text-left text-sm text-gray-700 last:border-b-0 hover:bg-[#f3f9ff]"
              >
                <Folder className="h-4 w-4 text-[#ffb100]" />
                <span className="flex-1 truncate">{it.name}</span>
                <ChevronRight className="h-3.5 w-3.5 text-gray-400" />
              </button>
            ))
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} className="border-gray-300 text-gray-700">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="folder-picker-confirm" className="bg-[#0364b8] text-white hover:bg-[#0358a0]">
            {busy ? "Working…" : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FolderPicker;
